import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { Badge, Divider, Flex, Layout, Segmented, Typography } from 'antd';
import { SegmentedLabeledOption, SegmentedValue } from 'antd/es/segmented';
import { AxiosResponse } from 'axios';
import { t } from 'i18next';
import { Fragment, ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getWaitingProcessingTicketCount } from 'src/apis/helpdesk/ticket.api';
import { getAdvancePaymentWaitingProcessingCount } from 'src/apis/hr_ticket/advancePaymentTicket.api';
import {
  getBusinessTripTicketHrConfirmCount,
  getBusinessTripWaitingProcessingCount
} from 'src/apis/hr_ticket/businessTripTicket.api';
import { getEquipmentSupplyRequestWaitingProcessingCount } from 'src/apis/hr_ticket/equipmentSupplyRequestTicket.api';
import { getLeaveTicketHrConfirmCount, getLeaveTicketWaitingProcessingCount } from 'src/apis/hr_ticket/leaveTicket.api';
import {
  getOvertimeTicketHrConfirmCount,
  getOvertimeTicketWaitingProcessingCount
} from 'src/apis/hr_ticket/overtimeTicket.api';
import { getPurchaseRequestWaitingProcessingCount } from 'src/apis/hr_ticket/purchaseRequestTicket.api';
import {
  getTransportationHrConfirmCount,
  getTransportationWaitingProcessingCount
} from 'src/apis/hr_ticket/transportationTicket.api';
import { getHandoverTicketWaitingProcessingCount } from 'src/apis/office-ticket/handoverTicket.api';
import LeftIcon from 'src/assets/icons/left.png';
import RightIcon from 'src/assets/icons/right.png';
import urlPath from 'src/constants/urlPath';
import { SegmentedEn } from 'src/constants/segmentedEn.enum';
import { TransportationTypeEn } from 'src/constants/transportationType.enum';
import { AppContext } from 'src/contexts/app.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { useBasePath } from 'src/hooks/useBasePath';
import Icon from '../Icon';
import Menu from '../Menu/Menu';
import './sidebar.scss';
import {
  getAdditionalWorkingTimeTicketWaitingProcessingCount,
  getAdditionalWorkingTimeTicketHrConfirmCount
} from 'src/apis/hr_ticket/additionalWorkingTimeTicket.api';
import { UserInfo } from 'src/types/master_data/employee.type';
import { DepartmentEn } from 'src/constants/department.enum';
import {
  getTicketHrConfirmCount,
  getTicketWaitingProcessingCount
} from 'src/apis/hr_ticket/expenseTrackingRequestTicket.api';
import {
  getResignationTicketHrConfirmCount,
  getResignationTicketWaitingProcessingCount
} from 'src/apis/hr_ticket/resignationTicket.api';

const segmentedOptions = (
  segmentedCountQuery: UseQueryResult<AxiosResponse<any, any> | undefined, unknown>
): (SegmentedValue | SegmentedLabeledOption)[] => {
  return [
    {
      label: (
        <span>
          Ticket của tôi
          {!!segmentedCountQuery.data?.data.myTicket && <Badge status='processing' />}
        </span>
      ),
      value: SegmentedEn.myTicket
    },
    {
      label: (
        <span>
          Xử lý
          {!!segmentedCountQuery.data?.data.handlerTicket && <Badge status='processing' />}
        </span>
      ),
      value: SegmentedEn.handle
    }
  ];
};

const segmentedOptionsForHrTicket = (
  segmentedCountQuery: UseQueryResult<AxiosResponse<any, any> | undefined, unknown>
): (SegmentedValue | SegmentedLabeledOption)[] => {
  return [
    {
      label: (
        <span>
          Phiếu của tôi
          {!!segmentedCountQuery.data?.data.myTicket && <Badge status='processing' />}
        </span>
      ),
      value: SegmentedEn.myTicket
    },
    {
      label: (
        <span>
          Xử lý
          {!!segmentedCountQuery.data?.data.handlerTicket && <Badge status='processing' />}
        </span>
      ),
      value: SegmentedEn.handle
    }
  ];
};

const segmentedOptionsForHRConfirm = (
  segmentedCountQuery: UseQueryResult<AxiosResponse<any, any> | undefined, unknown>,
  userInfo: UserInfo | null,
  basePath?: string
): (SegmentedValue | SegmentedLabeledOption)[] => {
  if (basePath === urlPath.hrConfirmTransportationTicket.location && userInfo?.departmentId === DepartmentEn.hr) {
    return [
      {
        label: (
          <div>
            {renderHrTitle(basePath)}
            {!!segmentedCountQuery.data?.data?.waitingConfirm && <Badge status='processing' />}
          </div>
        ),
        value: SegmentedEn.hrConfirm
      }
    ];
  } else if (basePath?.includes(urlPath.hrConfirmAdditionalWorkingTimeTicket.location)) {
    return [
      {
        label: (
          <div>
            {renderHrTitle(basePath)}
            {!!segmentedCountQuery.data?.data?.pending && <Badge status='processing' />}
          </div>
        ),
        value: SegmentedEn.hrConfirm
      }
    ];
  } else {
    return [
      {
        label: (
          <div>
            {renderHrTitle(basePath)}
            {!!segmentedCountQuery.data?.data?.countHrConfirmTicketStatus?.waitingConfirm && (
              <Badge status='processing' />
            )}
          </div>
        ),
        value: SegmentedEn.hrConfirm
      }
    ];
  }
};

const renderHrTitle = (basePath?: string): ReactNode => {
  if (basePath?.includes(urlPath.hrConfirmLeaveTicket.location)) {
    return <Text>Xác nhận nghỉ phép</Text>;
  } else if (basePath?.includes(urlPath.hrConfirmOvertimeTicket.location)) {
    return <Text>Xác nhận tăng ca</Text>;
  } else if (basePath?.includes(urlPath.hrConfirmBusinessTripTicket.location)) {
    return <Text>Xác nhận công tác</Text>;
  } else if (basePath?.includes(urlPath.hrConfirmTransportationTicket.location)) {
    return <Text>Xác nhận vận chuyển</Text>;
  } else if (basePath?.includes(urlPath.hrConfirmResignationTicket.location)) {
    return <Text>Xác nhận nghỉ việc</Text>;
  } else if (basePath?.includes(urlPath.hrConfirmAdditionalWorkingTimeTicket.location)) {
    return <Text>Bổ sung chấm công</Text>;
  } else return <Fragment />;
};

const { Sider: SiderAntd } = Layout;
const { Text } = Typography;

const Sidebar = () => {
  const [segmented, setSegmented] = useState<SegmentedEn | undefined>();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = useBasePath();
  const isHrConfirm = location.pathname.includes(urlPath.hrConfirm.location);
  const isOfficeTicket = location.pathname.includes(urlPath.corporateOperation.location);
  const isTicket = location.pathname.includes(urlPath.ticket.location);
  const isHrTicket = location.pathname.includes(urlPath.hrTicket.location);
  const { isSidebarCollapsed, userInfo, setIsTicketDetailCollapsed, setIsSidebarCollapsed } = useContext(AppContext);
  const { rangeDate, setApprovalStatus, setTicketStatusId } = useContext(TicketContext);

  const handleCallApi = () => {
    const ticketTypes = [
      { path: urlPath.ticket.location, apiFunc: getWaitingProcessingTicketCount },
      { path: urlPath.resignationTicket.location, apiFunc: getResignationTicketWaitingProcessingCount },
      { path: urlPath.leaveTicket.location, apiFunc: getLeaveTicketWaitingProcessingCount },
      { path: urlPath.overtimeTicket.location, apiFunc: getOvertimeTicketWaitingProcessingCount },
      { path: urlPath.expenseTrackingRequestTicket.location, apiFunc: getTicketWaitingProcessingCount },
      { path: urlPath.businessTripTicket.location, apiFunc: getBusinessTripWaitingProcessingCount },
      { path: urlPath.advancePaymentTicket.location, apiFunc: getAdvancePaymentWaitingProcessingCount },
      { path: urlPath.handoverTicket.location, apiFunc: getHandoverTicketWaitingProcessingCount },
      { path: urlPath.purchaseRequestTicket.location, apiFunc: getPurchaseRequestWaitingProcessingCount },
      { path: urlPath.transportationTicket.location, apiFunc: getTransportationWaitingProcessingCount },
      {
        path: urlPath.equipmentSupplyRequestTicket.location,
        apiFunc: getEquipmentSupplyRequestWaitingProcessingCount
      },
      {
        path: urlPath.additionalWorkingTimeTicket.location,
        apiFunc: getAdditionalWorkingTimeTicketWaitingProcessingCount
      },
      { path: urlPath.hrConfirmLeaveTicket.location, apiFunc: getLeaveTicketHrConfirmCount },
      { path: urlPath.hrConfirmResignationTicket.location, apiFunc: getResignationTicketHrConfirmCount },
      { path: urlPath.hrConfirmBusinessTripTicket.location, apiFunc: getBusinessTripTicketHrConfirmCount },
      { path: urlPath.hrConfirmOvertimeTicket.location, apiFunc: getOvertimeTicketHrConfirmCount },
      { path: urlPath.hrConfirmExpenseTrackingRequestTicket.location, apiFunc: getTicketHrConfirmCount },
      {
        path: urlPath.hrConfirmTransportationTicket.location,
        apiFunc: getTransportationHrConfirmCount,
        params: `${TransportationTypeEn.selfSufficient},${TransportationTypeEn.shuttle},${TransportationTypeEn.rentalCar}`
      },
      {
        path: urlPath.hrConfirmAdditionalWorkingTimeTicket.location,
        apiFunc: getAdditionalWorkingTimeTicketHrConfirmCount
      }
    ];

    const ticketType = ticketTypes.find((ticketType) => basePath?.includes(ticketType.path));

    if (!ticketType) return;

    return ticketType.apiFunc({ fromDate: rangeDate.fromDate, toDate: rangeDate.toDate }, String(ticketType.params));
  };

  const segmentedCountQuery = useQuery({
    queryKey: ['segmentedStatusCount', basePath, rangeDate.fromDate, rangeDate.toDate],
    queryFn: () => handleCallApi(),
    staleTime: 60 * 1000,
    keepPreviousData: true
  });

  useEffect(() => {
    const path = location.pathname;
    const pathSegmentedPairs = [
      {
        paths: [
          urlPath.myTicket.location,
          urlPath.myAdvancePaymentTicket.location,
          urlPath.myLeaveTicket.location,
          urlPath.myLeavePackage.location,
          urlPath.myOvertimeTicket.location,
          urlPath.myExpenseTrackingRequestTicket.location,
          urlPath.myHandoverTicket.location,
          urlPath.myTransportationTicket.location,
          urlPath.myBusinessTripTicket.location,
          urlPath.myPurchaseRequestTicket.location,
          urlPath.myEquipmentSupplyRequestTicket.location,
          urlPath.myAdditionalWorkingTimeTicket.location,
          urlPath.myResignationTicket.location
        ],
        segmented: SegmentedEn.myTicket
      },
      {
        paths: [
          urlPath.hrConfirmLeaveTicket.location,
          urlPath.hrConfirmBusinessTripTicket.location,
          urlPath.hrConfirmOvertimeTicket.location,
          urlPath.hrConfirmExpenseTrackingRequestTicket.location,
          urlPath.hrConfirmTransportationTicket.location,
          urlPath.hrConfirmResignationTicket.location,
          urlPath.hrConfirmAdditionalWorkingTimeTicket.location
        ],
        segmented: SegmentedEn.hrConfirm
      }
    ];

    const pair = pathSegmentedPairs.find((item) => item.paths.some((p) => path.includes(p)));

    setSegmented(pair ? pair.segmented : SegmentedEn.handle);
  }, [location.pathname]);

  const handleCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleChangeSegmented = (value: SegmentedValue) => {
    let url = basePath?.split('/');
    url?.pop();
    // Xử lý cho additionalWorkingTimeTicket package
    // if (basePath?.includes(urlPath.additionalWorkingTimeTicket.location)) {
    //   if (value === SegmentedEn.handle) {
    //     navigate(`${url?.join('/')}/approval-package`);
    //   } else if (value === SegmentedEn.myTicket) {
    //     navigate(`${url?.join('/')}/my-package`);
    //   } else {
    //     navigate(`${url?.join('/')}/my-ticket`);
    //   }
    // } else
    if (value === SegmentedEn.handle && (isHrTicket || isTicket)) {
      navigate(`${url?.join('/')}/approval`);
    } else if (value === SegmentedEn.handle && isOfficeTicket) {
      navigate(`${url?.join('/')}/confirm`);
    } else {
      navigate(`${url?.join('/')}/my-ticket`);
    }
    setSegmented(value as SegmentedEn);
    setIsTicketDetailCollapsed(true);
    setTicketStatusId(undefined);
    setApprovalStatus(undefined);
  };

  const sidebarTitle = () => {
    const path = location.pathname;
    const pathTitlePairs = [
      { path: urlPath.ticket.location, title: 'v2.sidebar.ticket.index' },
      { path: urlPath.advancePaymentTicket.location, title: 'v2.sidebar.hrTicket.advancePaymentTicket' },
      { path: urlPath.leaveTicket.location, title: 'v2.sidebar.hrTicket.leaveTicket' },
      { path: urlPath.overtimeTicket.location, title: 'v2.sidebar.hrTicket.overtimeTicket' },
      {
        path: urlPath.expenseTrackingRequestTicket.location,
        title: 'v2.sidebar.hrTicket.expenseTrackingRequestTicket'
      },
      { path: urlPath.handoverTicket.location, title: 'v2.sidebar.hrTicket.handoverTicket' },
      { path: urlPath.transportationTicket.location, title: 'v2.sidebar.hrTicket.transportationTicket' },
      { path: urlPath.businessTripTicket.location, title: 'v2.sidebar.hrTicket.businessTripTicket' },
      { path: urlPath.purchaseRequestTicket.location, title: 'v2.sidebar.hrTicket.purchaseRequestTicket' },
      {
        path: urlPath.equipmentSupplyRequestTicket.location,
        title: 'v2.sidebar.hrTicket.equipmentSupplyRequestTicket'
      },
      {
        path: urlPath.additionalWorkingTimeTicket.location,
        title: 'v2.sidebar.hrTicket.additionalWorkingTimeTicket'
      },
      { path: urlPath.resignationTicket.location, title: 'v2.sidebar.hrTicket.resignationTicket' },

      { path: urlPath.hrConfirmLeaveTicket.location, title: 'v2.sidebar.hrConfirm.leaveTicket' },
      { path: urlPath.hrConfirmBusinessTripTicket.location, title: 'v2.sidebar.hrConfirm.businessTripTicket' },
      { path: urlPath.hrConfirmOvertimeTicket.location, title: 'v2.sidebar.hrConfirm.overtimeTicket' },
      {
        path: urlPath.hrConfirmExpenseTrackingRequestTicket.location,
        title: 'v2.sidebar.hrConfirm.expenseTrackingRequestTicket'
      },
      { path: urlPath.hrConfirmTransportationTicket.location, title: 'v2.sidebar.hrConfirm.transportationTicket' },
      { path: urlPath.hrConfirmResignationTicket.location, title: 'v2.sidebar.hrConfirm.resignationTicket' },
      {
        path: urlPath.hrConfirmAdditionalWorkingTimeTicket.location,
        title: 'v2.sidebar.hrConfirm.additionalWorkingTimeTicket'
      }
    ];

    const pair = pathTitlePairs.find((item) => path.includes(item.path));

    return pair ? t(pair.title) : null;
  };

  return (
    <SiderAntd
      trigger={null}
      width={'260px'}
      collapsible
      collapsed={isSidebarCollapsed}
      collapsedWidth={50}
      className='sidebar-main'
    >
      {!isSidebarCollapsed ? (
        <Fragment>
          <Flex justify='space-between' align='center' gap={10}>
            <Text strong className='sidebar-title'>
              {sidebarTitle()}
            </Text>
            <div className='menu-button menu-button-open' onClick={handleCollapse}>
              <Icon url={LeftIcon} size={20} titleTooltip='Menu' />
            </div>
          </Flex>
          <Divider dashed className='sidebar-title-divider' />
          <Segmented
            options={
              isHrConfirm
                ? segmentedOptionsForHRConfirm(segmentedCountQuery, userInfo, basePath)
                : isTicket
                ? segmentedOptions(segmentedCountQuery)
                : segmentedOptionsForHrTicket(segmentedCountQuery)
            }
            className='sidebar-segmented'
            value={segmented}
            onChange={(value) => handleChangeSegmented(value)}
            size='middle'
            block
          />
          <Menu segmented={segmented ?? SegmentedEn.myTicket} />
        </Fragment>
      ) : (
        <Fragment>
          <Flex
            justify='flex-end'
            align='center'
            gap={10}
            className='menu-button menu-button-open'
            onClick={handleCollapse}
          >
            <Icon
              url={RightIcon}
              size={20}
              titleTooltip='Xem menu'
              arrowTooltip={false}
              placementTooltip='right'
              styleTooltip={{ fontSize: '12px' }}
            />
          </Flex>
        </Fragment>
      )}
    </SiderAntd>
  );
};

export default Sidebar;
